import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { WaitingRoomView } from "src/api/types";
import DataCollapse from "src/shared/DataCollapse";
import RoomStatus from "src/components/RoomStatus";
import { columns } from "src/pages/WaitingRoomTracker/utils/columns";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { useGetTasksForWaitingRoomQuery } from "src/api/services/tasksApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { Paths } from "src/constants/paths";
import { useColumns } from "src/components/TaskHistory/useColumns";

interface TableRowProps {
  row: WaitingRoomView;
}

const WaitingRoomTableRow = ({ row }: TableRowProps) => {
  const taskColumns = useColumns();
  const [display, setDisplay] = useState<number | null>(null);
  const { selected, setSelected } = useSelection();
  const { data: tasks = [], isLoading } = useGetTasksForWaitingRoomQuery(
    display != null
      ? { patientId: row.patient_id!, queue: row.queue! }
      : skipToken,
  );

  const isSelected = useMemo(() => {
    return selected.includes(row.id);
  }, [selected, row.id]);

  const handleSelect = useCallback(() => {
    setSelected(
      selected.includes(row.id)
        ? selected.filter((id) => id !== row.id)
        : [...selected, row.id],
    );
  }, [row.id, selected, setSelected]);

  const handleCollapse = useCallback(
    () => setDisplay(() => (display === row.id ? null : row.id)),
    [display, row.id],
  );

  return (
    <>
      <TableRow hover role="checkbox" aria-label="task row" tabIndex={-1}>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            aria-label="select room"
            onChange={handleSelect}
            checked={isSelected}
            disabled={["COMPLETED", "CANCELED"].includes(row.status)}
          />
        </TableCell>
        {columns.map((column) => {
          const value = column.format(row);
          return (
            <TableCell
              key={column.id}
              align={column.align}
              sx={{ minWidth: column.minWidth }}
            >
              {column.id === "patient_id" ? (
                <Link
                  to={`${Paths.DASHBOARD_PATIENTS}/${value}`}
                  target="_blank"
                >
                  {value}
                </Link>
              ) : column.id === "status" ? (
                <RoomStatus status={row.status} />
              ) : (
                <Typography>{value}</Typography>
              )}
            </TableCell>
          );
        })}

        <TableCell>
          <IconButton onClick={handleCollapse}>
            {display === row.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={columns.length + 2}
        >
          <DataCollapse
            onCollapse={display === row.id}
            data={tasks}
            loading={isLoading}
            primaryKey={"id"}
            columns={taskColumns}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export default WaitingRoomTableRow;
